import createFetchival from '@exodus/fetch/create-fetchival'
import { create } from 'superstruct'
import { getName } from 'country-list'
import { filterByCountries, joinUrl, omitUndefinedValues } from '../utils.js'
import {
  ASSET_TYPES,
  FILTER_LIST_MODES,
  ORDER_TYPES,
  PAYMENT_METHOD_TYPES,
  PROVIDERS,
} from '../constants.js'
import { createPaymentMethodValidator } from '../payment-method.js'
import { ERROR_PAYMENT_METHOD_NOT_SUPPORTED } from '../errors.js'
import { Quote } from '../schema/quote.js'
import { APP_URL, ENABLED_ASSETS, PAYMENT_METHODS } from './constants.js'
import paypalAdapter from './adapter.js'

export * from './constants.js'
export * from './utils.js'

// eslint-disable-next-line no-undef
const globalFetch = fetch.bind(globalThis)

const paypalProvider = ({
  assetsModule,
  countries = {},
  assets: assets_ = ENABLED_ASSETS,
  countriesMode = FILTER_LIST_MODES.blacklist,
  enabled = true,
  url = APP_URL,
  fetch = globalFetch,
  paymentMethodTypes,
} = {}) => {
  const fetchival = createFetchival({ fetch })
  const apiUrl = joinUrl(url, 'api', 'v1', 'paypal')
  const fetch_ = fetchival(apiUrl)

  const createOrder = async (order) => {
    const order_ = paypalAdapter.fromExodusToProviderOrder(order, assetsModule)

    const { id: providerId, redirect_url: redirectUrl } =
      await fetch_('crypto/order-entries').post(order_)

    return {
      providerId,
      redirectUrl,
    }
  }

  const getAssets = async () => {
    const { configurations } = await fetch_('crypto/configurations').get()

    const availableAssets = []
    for (const configuration of configurations) {
      for (const assetSymbol of configuration.asset_symbols) {
        for (const networkName of configuration.blockchains) {
          const name = paypalAdapter.fromProviderToExodusAsset({
            assets_,
            assetsModule,
            assetSymbol,
            networkName,
          })?.name

          if (!name) {
            continue
          }

          availableAssets.push({
            isBuySupported: true,
            isSellSupported: false,
            minBuyAmount: 0,
            maxBuyAmount: Number.MAX_SAFE_INTEGER,
            name,
            type: ASSET_TYPES.crypto,
          })
        }
      }
    }

    const uniqueCurrencies = new Set()
    const availableCurrencies = configurations
      .flatMap(({ attributes }) =>
        attributes?.currencies?.map((name) => ({
          isBuySupported: true,
          isSellSupported: false,
          name,
          minBuyAmount: attributes?.min_limit?.amount ?? 0,
          maxBuyAmount: attributes?.max_limit?.amount ?? Number.MAX_SAFE_INTEGER,
          type: ASSET_TYPES.fiat,
        }))
      )
      .filter((asset) => {
        const { name } = asset
        return !name || uniqueCurrencies.has(name) ? false : uniqueCurrencies.add(name)
      })

    return [...availableCurrencies, ...availableAssets]
  }

  const getCountries = async ({ countryCode, regionCode } = Object.create(null)) => {
    const { configurations } = await fetch_('crypto/configurations').get()

    return configurations.flatMap((value) => {
      if (regionCode) {
        const { includes, excludes } = value.attributes?.regions

        if (!includes?.include_all && !includes?.regions?.includes(regionCode)) {
          return []
        }

        if (excludes?.regions?.includes(regionCode)) {
          return []
        }
      }

      return value.countries
        .filter(
          (code) =>
            !filterByCountries(countries, code === countryCode ? [countryCode, regionCode] : code, {
              mode: countriesMode,
              properties: [ORDER_TYPES.buy],
            })
        )
        .map((code) => ({
          code,
          isBuySupported: true,
          isSellSupported: false,
          name: getName(code),
        }))
    })
  }

  const getOrder = async (providerId, { serverClient, serverOrder, type }) => {
    const { id: orderId, fromAmount } = serverOrder || {}
    const {
      orders: [order],
    } = await fetch_(`crypto/orders`).get({
      order_entry_id: providerId,
    })

    const exodusOrder = paypalAdapter.fromProviderToExodusOrder(
      {
        fromAmount,
        id: providerId,
        assetName: type === ORDER_TYPES.buy ? serverOrder.toAssetName : serverOrder.fromAssetName,
        ...order,
      },
      assetsModule,
      assets_
    )

    const isStatusChanged = serverOrder?.status !== order?.status && orderId
    if (isStatusChanged && order?.status) {
      const { fee, networkFee, rate, status, to, totalFee, txId } = exodusOrder
      await serverClient.updateOrder(orderId, {
        status,
        provider: PROVIDERS.paypal,
        ...omitUndefinedValues({
          providerId,
          exchangeRate: rate || undefined,
          providerFee: fee || undefined,
          networkFee: networkFee || undefined,
          totalFee: totalFee || undefined,
          toAmount: to.amount || undefined,
          userTxHash: txId || undefined,
        }),
      })
    }

    return exodusOrder
  }

  const getQuote = async ({
    countryCode,
    fromAmount,
    fromAsset,
    paymentMethod = PAYMENT_METHOD_TYPES.paypal,
    toAsset,
    type = ORDER_TYPES.buy,
  } = {}) => {
    const providerPaymentMethod = getPaymentMethod(paymentMethod, {
      assets: assets_,
      country: countryCode,
      currency: fromAsset,
    })

    if (type !== ORDER_TYPES.buy) {
      throw new Error('Only `buy` orders are accepted')
    }

    if (!providerPaymentMethod) {
      throw new Error(ERROR_PAYMENT_METHOD_NOT_SUPPORTED.message)
    }

    const { configurations } = await fetch_('crypto/configurations').get()
    let minBuyAmount = 0
    let maxBuyAmount = Number.MAX_SAFE_INTEGER

    const assetSymbol = assetsModule.getAsset(toAsset)?.displayTicker
    configurations.flatMap((value) => {
      if (value.asset_symbols.includes(assetSymbol)) {
        minBuyAmount = value.attributes?.min_limit?.amount ?? 0
        maxBuyAmount = value.attributes?.max_limit?.amount ?? Number.MAX_SAFE_INTEGER
      }
    })

    return create(
      {
        from: {
          asset: fromAsset,
          amount: fromAmount,
          minBuyAmount,
          maxBuyAmount,
        },
        to: {
          asset: toAsset,
        },
        paymentMethod: providerPaymentMethod,
        provider: PROVIDERS.paypal,
        type,
      },
      Quote
    )
  }

  const getPaymentMethod = (paymentMethod, parameters = Object.create(null)) => {
    const providerPaymentMethod = [...PAYMENT_METHODS.values()].find(
      createPaymentMethodValidator({
        ...parameters,
        assets: assets_,
        type: paymentMethod,
        paymentMethodTypes,
      })
    )

    return providerPaymentMethod?.id
  }

  return {
    createOrder,
    name: PROVIDERS.paypal,
    enabled,
    getAssets,
    getCountries,
    getOrder,
    getPaymentMethod,
    getQuote,
    orderTypes: [ORDER_TYPES.buy],
  }
}

export default paypalProvider
